import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {flyoutNavigationRecipe} from '../../components/navigation/flyout/partials/flyout-navigation.recipe'
import {previewNavigationRecipe, RESERVE_LABEL_MAP} from './index.recipe'

/**
 * The flyout's desktop pane row, for a preview holding one pane rather than the
 * whole tree.
 *
 * The row declares each pane's share, so the preview reuses it and reserves the
 * missing level's column with an empty box. Desktop-only: mobile shows one pane
 * at a time, with no column to stand in for.
 *
 * @component
 * @param {Object} props
 * @param {'level1'|'level2'} props.reserve - The level whose column to reserve.
 * @param {React.ReactNode} props.children - The pane on show.
 * @returns {JSX.Element}
 */
const NavigationPreviewPanes = ({reserve, children}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    const previewRecipe = useSlotRecipe({recipe: previewNavigationRecipe})
    const previewStyles = useMemo(() => previewRecipe({reserve}), [previewRecipe, reserve])

    // Named rather than left blank: an empty column reads as a layout fault,
    // while a labelled one reads as the level this section is not about.
    const spacer = (
        <Box aria-hidden="true" css={previewStyles.spacer}>
            {RESERVE_LABEL_MAP[reserve]}
        </Box>
    )

    // Reserving level 1 leaves level 2 on show, which takes the rest of the row
    // and needs a flex item of its own. Level 1's own container is
    // `display: contents` on desktop, so its list is already the flex item.
    return (
        <Box css={styles.panes}>
            {reserve === 'level1' ? (
                <>
                    {spacer}
                    <Box css={previewStyles.paneItem}>{children}</Box>
                </>
            ) : (
                <>
                    {children}
                    {spacer}
                </>
            )}
        </Box>
    )
}

NavigationPreviewPanes.propTypes = {
    reserve: PropTypes.oneOf(['level1', 'level2']).isRequired,
    children: PropTypes.node
}

export default NavigationPreviewPanes
